import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from "react";
import { C, mono } from "../theme.ts";

// Pull down at the top of a screen to fetch it again. T-193.
//
// ───────────────────────────────────────────────────────────────────────────
// Why this exists at all, given the board is a web app
//
// This board is written from four directions — this project's chats, Claude
// Code, the in-app assistant, and the conflict detector, which raises and
// retires cards on its own every fifteen minutes. The screen goes out of date
// while Karim does nothing. In a browser that is a reload; installed to the
// home screen there is no browser chrome to reload from, and this is the
// gesture that replaces it.
//
// Never build here: a "last updated" line, a sync indicator, a dot that means
// stale, or a refresh on focus. All four are a screen reporting on how up to
// date you are, which is the same verdict the pantry and the slots are written
// without. The gesture is asked for, it happens, it finishes, it says nothing
// afterwards.
//
// ── What this is not for
//
// It is not how a change Karim just made appears. A tick, a date, a task
// marked done all show instantly where they were made, with no gesture. If
// something on this board only updates after a pull, that is a bug in the
// thing that wrote it, not a feature of this file. Refresh is for changes made
// somewhere else.
//
// ── The one CSS line this depends on
//
// `overscroll-behavior-y: none` on `html, body` in index.html. It is what
// stops Chrome on Android running its own pull-to-refresh over the top of this
// one. It reads like app-shell furniture and it is load-bearing here: remove
// it and the screen gets two competing gestures on one thumb.
// ───────────────────────────────────────────────────────────────────────────

/** How far the screen has to come down before letting go does anything. */
const THRESHOLD = 64;
/** The furthest it will come down, however hard it is pulled. */
const CEILING = 96;
/** The thumb moves further than the screen does, which is what makes it feel attached. */
const RESISTANCE = 0.45;
/**
 * The least time "refreshing" stays on screen.
 *
 * The brief's question — what happens at the top of a screen that is already
 * up to date. On a fast connection the answer without this is a line that
 * appears and vanishes inside one frame, which reads as the gesture having
 * failed rather than having found nothing to do. So a refresh that finds
 * nothing looks exactly like one that finds something: it takes a moment, and
 * then it is over. That is the whole of the "nothing changed" state — there is
 * no message saying so, because that would be the sync indicator this is
 * written without.
 */
const DWELL = 500;

type Handler = () => unknown | Promise<unknown>;

const Register = createContext<((h: Handler) => () => void) | null>(null);

/**
 * A view that fetches its own data registers it here, and the pull will
 * include it. Views drawn from the board do not need this and must not call
 * it: the board is refetched on every pull regardless, so registering would
 * only fetch it twice.
 */
export function useRefresh(handler: Handler) {
  const register = useContext(Register);
  // Held in a ref so a handler rebuilt on every render does not churn the
  // registration, and so what runs is always the current one.
  const held = useRef(handler);
  held.current = handler;
  useEffect(() => register?.(() => held.current()), [register]);
}

export function PullToRefresh({
  onRefresh, enabled, background, children,
}: {
  /** The board. Always refetched, whatever tab is open. */
  onRefresh: () => Promise<void>;
  /** False while a sheet or the menu owns the screen. */
  enabled: boolean;
  /**
   * The colour behind the "pull to refresh" label, painted directly rather
   * than left to whatever this component's ancestor happens to show through.
   * It used to be left transparent, which meant the gap above the pulled
   * content revealed `Shell`'s cockpit ivory even on Today, which paints its
   * own content a different colour — the old skin bleeding through under the
   * new one. The caller knows which colour the content it is passing down
   * actually sits on; this does not guess it.
   */
  background: string;
  children: React.ReactNode;
}) {
  const [pull, setPull] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);

  const handlers = useRef(new Set<Handler>());
  const start = useRef<number | null>(null);
  // The touch listeners are bound once and read these rather than closing over
  // state, so the gesture never rebinds mid-pull.
  const distance = useRef(0);
  const working = useRef(false);

  const register = useCallback((h: Handler) => {
    handlers.current.add(h);
    return () => { handlers.current.delete(h); };
  }, []);

  const run = useCallback(async () => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    const held = new Promise((r) => setTimeout(r, DWELL));
    // The board, plus whatever the open view fetches for itself. In parallel,
    // and each failure kept to itself: a view that cannot reach the server
    // already says so in its own words, and one that fails should not stop the
    // rest of the screen updating.
    await Promise.all([
      Promise.resolve().then(onRefresh).catch(() => {}),
      ...[...handlers.current].map((h) => Promise.resolve().then(h).catch(() => {})),
    ]);
    await held;
    working.current = false;
    distance.current = 0;
    setBusy(false);
    setPull(0);
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled) return;

    const give = () => {
      start.current = null;
      distance.current = 0;
      setPull(0);
      setDragging(false);
    };

    const down = (e: TouchEvent) => {
      if (working.current || e.touches.length !== 1 || window.scrollY > 0) {
        start.current = null;
        return;
      }
      start.current = e.touches[0].clientY;
      distance.current = 0;
    };

    const move = (e: TouchEvent) => {
      if (start.current === null || working.current) return;
      const dy = e.touches[0].clientY - start.current;
      // An upward drag, or the page having scrolled under us, is an ordinary
      // scroll. Hand the touch back rather than half-owning it — this is the
      // mistake that made the old card drag fight the scroll.
      if (dy <= 0 || window.scrollY > 0) { give(); return; }
      const d = Math.min(CEILING, dy * RESISTANCE);
      distance.current = d;
      setPull(d);
      setDragging(true);
      // Only once the pull is unmistakably a pull, and only on a cancelable
      // move. Below that the browser keeps the touch.
      if (d > 3 && e.cancelable) e.preventDefault();
    };

    const up = () => {
      if (start.current === null) return;
      const reached = distance.current >= THRESHOLD;
      start.current = null;
      setDragging(false);
      if (reached) run(); else { distance.current = 0; setPull(0); }
    };

    window.addEventListener("touchstart", down, { passive: true });
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    window.addEventListener("touchcancel", give);
    return () => {
      window.removeEventListener("touchstart", down);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
      window.removeEventListener("touchcancel", give);
    };
  }, [enabled, run]);

  // A pull interrupted by a sheet opening should not leave the screen held
  // down with nothing able to release it.
  useEffect(() => {
    if (!enabled && !working.current) { start.current = null; distance.current = 0; setPull(0); setDragging(false); }
  }, [enabled]);

  const offset = busy ? 44 : pull;

  return (
    <Register.Provider value={register}>
      {/* No transform on this element — the bottom bar, the assistant and the
          sheets are `position: fixed` and are deliberately left outside it. A
          transformed ancestor would make them fixed to this box instead of to
          the window, and the nav would slide off with the pull. */}
      <div style={{ position: "relative" }}>
        <div
          aria-hidden
          style={{
            position: "absolute", top: 0, left: 0, right: 0, height: Math.max(offset, 0),
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: mono, fontSize: 11, letterSpacing: 1, background,
            color: busy || offset >= THRESHOLD ? C.inkFaint : C.inkGhost,
            opacity: offset > 8 ? 1 : 0,
            pointerEvents: "none", overflow: "hidden",
          }}
        >
          {busy ? "refreshing" : offset >= THRESHOLD ? "release" : "pull to refresh"}
        </div>
        <div style={{
          transform: `translateY(${offset}px)`,
          transition: dragging ? "none" : "transform 260ms cubic-bezier(.22,.61,.36,1)",
          willChange: "transform",
        }}>{children}</div>
      </div>
    </Register.Provider>
  );
}
